import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { userApi } from '@app/store';
import { IUser, UserDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { UserForm } from './UserForm';
import { UserFormValues, transformToUpdatableFormUser, transformUserFormValuesToPayload } from './user-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { UserCard } from './UserCard';
import { CheckPermissions } from '@app/components/CheckPermissions';

interface UserUpdateProps {
  user?: Partial<IUser>;
  isFetching: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (user: Partial<IUser>) => void;
}

export const UserUpdate = ({ user, isFetching, toggle, onDelete }: UserUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = userApi.useUpdateOneUsersMutation();

  const formMethods = useForm<UserFormValues>({
    resolver: classValidatorResolver(UserDTO),
    defaultValues: transformToUpdatableFormUser(user)
  });

  const onUpdate = async (formValues: UserFormValues) => {
    try {
      const updatedUser = await update({ _id: user?._id!, ...transformUserFormValuesToPayload(formValues) }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedUser });

      toast.success('Votre utilisateur a bien été modifié.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre utilisateur.')
    }
  }

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormUser(user));
  }, [user, formMethods.reset]);

  return (
    <Fragment>
      <UserCard
        user={user}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>

          <Panel.FormContainer>

            <CheckPermissions permissions={['dashboard::users.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  disabled={!user}
                  bordered
                  important
                  onPress={() => onDelete(user!)} />
              </Panel.Header>
            </CheckPermissions>

            <UserForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
